import { useEffect } from 'react';
import TelemetryProvider, { Properties } from 'common/utilities/TelemetryProvider';
import { ApiCallResultStatus, isApiCallsComplete } from 'store/apiCallResultStatus';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { markTTI } from 'store/performance/performanceSlice';

import { performanceService } from './PerformanceService';
import { useScreenSizeStats } from './telemetryHelpers';

/**
 * Tracks page load and publishes performance telemetry
 * when all API calls associated with the given apiCallResultStatus are complete.
 *
 * @param pageName name of the page
 * @param apiCallResultStatus array of ApiCallResultStatus to check
 * @param additionalProps additional telemetry properties to log
 * @param isPageReady page load tracking will end when this additional parameter value is true
 */
export const usePageLoadTracker = (
  pageName: string,
  apiCallResultStatus: ApiCallResultStatus[],
  additionalProps: Properties = {},
  isPageReady = true
) => {
  const dispatch = useAppDispatch();
  const screenSizeStats = useScreenSizeStats();
  const { isPageLoadComplete } = useAppSelector((state) => state.performance);
  const shouldEndPageLoad = isApiCallsComplete(apiCallResultStatus) && isPageReady;

  useEffect(() => {
    performanceService.addSplitTiming(`${pageName}.Mount`);
  }, [pageName]);

  useEffect(() => {
    if (shouldEndPageLoad && !isPageLoadComplete) {
      performanceService.addSplitTiming(`${pageName}.LoadComplete`);
      const { timingData, logProperties } = performanceService.getTTIData();
      void dispatch(markTTI(timingData));
      TelemetryProvider.Instance.logUserActivity(pageName, 'PageViewActivity', {
        ...logProperties,
        ...screenSizeStats,
        ...additionalProps,
      });
    }
  }, [additionalProps, dispatch, screenSizeStats, isPageLoadComplete, pageName, shouldEndPageLoad]);
};
